import type { Ref } from 'vue'

interface AutocompleteSuggestion {
  value: string
}

interface DrupalAutocompleteController {
  suggestions: Ref<AutocompleteSuggestion[]>
  isLoading: Ref<boolean>
  fetchSuggestions: (query: string) => void
  clear: () => void
}

/**
 * Composable for fetching autocomplete suggestions from Drupal's
 * search_api_autocomplete module.
 *
 * Uses the CE API proxy so the Drupal backend URL is not exposed.
 *
 * @param searchId - The search_api_autocomplete search ID (e.g., 'search', 'news_listing')
 */
export function useDrupalAutocomplete(searchId: string): DrupalAutocompleteController {
  const { $ceApi } = useDrupalCe()
  const suggestions = ref<AutocompleteSuggestion[]>([])
  const isLoading = ref(false)

  let debounceTimer: ReturnType<typeof setTimeout> | null = null
  let abortController: AbortController | null = null

  function fetchSuggestions(query: string) {
    // Clear pending debounce.
    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }

    // Clear if query too short.
    if (!query || query.trim().length < 3) {
      suggestions.value = []
      isLoading.value = false
      return
    }

    isLoading.value = true

    debounceTimer = setTimeout(async () => {
      // Abort previous in-flight request.
      if (abortController) {
        abortController.abort()
      }
      abortController = new AbortController()

      try {
        // Skip the SSR API proxy (/api/drupal-ce) which rewrites the path.
        // Use direct mode so the request goes via /ce-api proxy which
        // preserves the full path.
        const fetchInstance = $ceApi({}, true)
        const data = await fetchInstance<AutocompleteSuggestion[]>(
          `/search_api_autocomplete/${searchId}`,
          {
            query: { q: query.trim() },
            signal: abortController.signal,
          }
        )

        suggestions.value = (data || []).slice(0, 5).map((item) => ({
          value: item.value,
        }))
      }
      catch (error: unknown) {
        // Ignore aborted requests.
        if (error instanceof Error && error.name === 'AbortError') return
        suggestions.value = []
      }
      finally {
        isLoading.value = false
      }
    }, 300)
  }

  function clear() {
    suggestions.value = []
    isLoading.value = false
    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }
    if (abortController) {
      abortController.abort()
    }
  }

  return {
    suggestions,
    isLoading,
    fetchSuggestions,
    clear,
  }
}
